// Moving in from Fullbay: pick all of Fullbay's "Manage Data" downloads at once. Each file is recognised by its columns,
// and they are merged into ready rows for the importer: a customer gets its email(s) and contact from the Contacts file and
// its address from the Addresses file; units, parts and vendors come straight across. Inactive records are left behind.

export type FbSheet = { name: string; headers: string[]; rows: string[][] };
export type FbKind = 'customers' | 'customers2' | 'contacts' | 'addresses' | 'units' | 'inventory' | 'avgcost' | 'vendors' | 'vendorAddresses' | 'unknown';
type Row = Record<string, string>;

const has = (h: string[], ...cols: string[]) => cols.every(c => h.includes(c));

export function fullbayKind(headers: string[]): FbKind {
  const h = headers.map(x => x.trim());
  if (has(h, 'Contact ID', 'Customer ID')) return 'contacts';
  if (has(h, 'Address ID', 'Customer ID')) return 'addresses';
  if (has(h, 'Address ID', 'Vendor ID')) return 'vendorAddresses';
  if (has(h, 'Unit ID', 'VIN')) return 'units';
  if (has(h, 'Part #', 'In Stock')) return 'inventory';
  if (has(h, 'Part #', 'Current Average Cost')) return 'avgcost';
  if (has(h, 'Vendor ID', 'Vendor Name')) return 'vendors';
  if (has(h, 'Customer ID', 'Company Name')) return 'customers';
  if (has(h, 'ID', 'Company Name', 'Physical Address Line 1')) return 'customers2';
  return 'unknown';
}

export const FB_LABEL: Record<FbKind, string> = {
  customers: 'Customers', customers2: 'Customers (with address)', contacts: 'Customer contacts (emails, phones)', addresses: 'Customer addresses',
  units: 'Trucks & trailers', inventory: 'Parts inventory', avgcost: 'Parts average cost (not needed)', vendors: 'Vendors', vendorAddresses: 'Vendor addresses', unknown: 'Not a Fullbay file we know',
};

const objs = (s: FbSheet): Row[] => s.rows.map(r => Object.fromEntries(s.headers.map((h, i) => [h.trim(), (r[i] ?? '').trim()])));
const yes = (x?: string) => /^(yes|true|1|active)$/i.test(String(x || '').trim());
const no = (x?: string) => /^(no|false|0|inactive)$/i.test(String(x || '').trim());
const termsDays = (x?: string) => { const m = String(x || '').match(/(\d{1,3})/); return m ? m[1] : ''; };
const num = (x?: string) => String(x || '').replace(/[$,\s]/g, '');

export type FullbayResult = { customers: Row[]; units: Row[]; parts: Row[]; vendors: Row[]; left: string[]; files: { name: string; kind: FbKind; rows: number }[] };

export function fromFullbay(sheets: FbSheet[]): FullbayResult {
  const by = (k: FbKind) => sheets.filter(s => fullbayKind(s.headers) === k).flatMap(objs);
  const files = sheets.map(s => ({ name: s.name, kind: fullbayKind(s.headers), rows: s.rows.length }));
  const left: string[] = [];

  // ── Customers: Customers + (Customers with address) + Contacts + Addresses, joined on Fullbay's customer ID
  const extra = new Map(by('customers2').map(r => [r['ID'], r]));
  const contacts = new Map<string, Row[]>();
  for (const c of by('contacts')) if (!no(c['Contact Active'])) contacts.set(c['Customer ID'], [...(contacts.get(c['Customer ID']) || []), c]);
  const addrs = new Map<string, Row[]>();
  for (const a of by('addresses')) if (!no(a['Address Active'])) addrs.set(a['Customer ID'], [...(addrs.get(a['Customer ID']) || []), a]);
  const base = by('customers');
  const list: Row[] = base.length ? base : [...extra.values()].map(r => ({ ...r, 'Customer ID': r['ID'] }));
  let inactiveCust = 0;
  const customers: Row[] = [];
  for (const r of list) {
    if (no(r['Customer Active'])) { inactiveCust++; continue; }
    const id = r['Customer ID'];
    const x: Row = extra.get(id) || {};
    const cs = contacts.get(id) || [];
    // Who gets invoices / approves estimates first; anyone with an email if nobody is marked.
    const billing = cs.filter(c => c['Email'] && (yes(c['Receives Invoices']) || yes(c['Receive and Authorize Estimates']) || yes(c['Default Authorizer']) || yes(c['Is Billing Contact'])));
    const emails = [...new Set((billing.length ? billing : cs.filter(c => c['Email'])).map(c => c['Email'].toLowerCase()))].slice(0, 5);
    const lead = cs.find(c => yes(c['Default Authorizer'])) || cs.find(c => yes(c['Preselected Contact'])) || cs[0];
    const ad = (addrs.get(id) || []).find(a => yes(a['Is Default Ship To Address'])) || (addrs.get(id) || [])[0];
    const street = ad ? [ad['Address Line 1'], ad['Address Line 2']].filter(Boolean).join(' ') : [x['Physical Address Line 1'], x['Physical Address Line 2']].filter(Boolean).join(' ');
    const name = r['Company Name'];
    customers.push({
      name, usdot: r['DOT #'] || x['DOT #'] || '',
      phone: r['Customer Main Phone'] || x['Customer Main Phone'] || lead?.['Phone'] || lead?.['Cell Phone'] || '',
      email: emails.join(', '),
      contact: lead ? [lead['First Name'], lead['Last Name']].filter(Boolean).join(' ') : '',
      address: street, city: ad ? ad['City'] : x['Physical Address City'] || '', state: ad ? ad['State'] : x['Physical Address State'] || '', zip: ad ? ad['Zip/Postal Code'] : x['Physical Address Zip/Postal Code'] || '',
      terms_days: termsDays(r['Credit Terms']),
      tax_exempt: no(r['Taxable']) ? '1' : '',
      notes: [r['Tax Exempt #'] && `Tax exempt # ${r['Tax Exempt #']}`, cs.length > 1 ? 'Contacts: ' + cs.map(c => [[c['First Name'], c['Last Name']].filter(Boolean).join(' '), c['Phone'] || c['Cell Phone'], c['Email']].filter(Boolean).join(' ')).join('; ') : ''].filter(Boolean).join(' · ').slice(0, 1900),
    });
  }
  if (inactiveCust) left.push(`${inactiveCust} inactive customers`);
  // Two Fullbay customers with the same USDOT are one company (often a typo'd copy): they become one customer,
  // and the second one's trucks go to it.
  const sameAs = new Map<string, string>();
  const dupNames: string[] = [];
  const firstByDot = new Map<string, string>();
  for (const c of customers) {
    const d = c.usdot.replace(/\D/g, '');
    if (!d) continue;
    const first = firstByDot.get(d);
    if (first) { sameAs.set(c.name.toLowerCase(), first); dupNames.push(`${c.name} → ${first}`); } else firstByDot.set(d, c.name);
  }
  if (sameAs.size) left.push(`${sameAs.size} duplicate customer${sameAs.size > 1 ? 's' : ''} (same USDOT) merged: ${dupNames.join(', ')}`);

  // ── Units
  let inactiveUnits = 0;
  const units: Row[] = [];
  for (const u of by('units')) {
    if (no(u['Unit Active']) || no(u['Customer Active'])) { inactiveUnits++; continue; }
    const vin = u['VIN'];
    const engine = [u['Engine Year'], u['Engine Make'], u['Engine Model']].filter(Boolean).join(' ');
    units.push({
      customer: sameAs.get(String(u['Customer']).toLowerCase()) || u['Customer'], unit: u['Number'] || u['Fleet #'] || u['Nickname'] || (vin ? vin.slice(-6) : ''), vin,
      year: u['Chassis Year'], make: u['Chassis Make'], model: u['Chassis Model'], type: [u['Type'], u['Subtype']].filter(Boolean).join(' '),
      plate: u['License Plate'], mileage: num(u['Mileage']),
      notes: [engine && `Engine: ${engine}`, u['Engine Serial #'] && `Engine serial ${u['Engine Serial #']}`, u['Nickname'] && u['Number'] && `Nickname: ${u['Nickname']}`].filter(Boolean).join(' · '),
    });
  }
  if (inactiveUnits) left.push(`${inactiveUnits} inactive trucks/trailers`);
  const owners = new Map<string, Set<string>>();
  for (const u of units) if (u.vin.length >= 11) owners.set(u.vin, (owners.get(u.vin) || new Set()).add(u.customer.toLowerCase()));
  const shared = [...owners.values()].filter(x => x.size > 1).length;
  if (shared) left.push(`${shared} VIN${shared > 1 ? 's are' : ' is'} listed under two customers in Fullbay (sold trucks?) — each truck is brought in once, under the first customer; move it if needed`);

  // ── Parts ("General" is Fullbay's default location — not a real bin)
  const avg = new Map(by('avgcost').map(r => [r['Part #'], r['Current Average Cost']]));
  let inactiveParts = 0;
  const parts: Row[] = [];
  for (const p of by('inventory')) {
    if (p['Status'] && !/^active$/i.test(p['Status'])) { inactiveParts++; continue; }
    const loc = p['Default Location'];
    const vendor = /^shop inventory/i.test(p['Preferred Vendor'] || '') ? '' : p['Preferred Vendor'];
    parts.push({
      sku: p['Part #'], name: p['Description'], stock: num(p['In Stock']), cost: num(p['Average Cost'] || avg.get(p['Part #'])), price: num(p['Selling Price']),
      vendor: vendor || '', bin: /^general$/i.test(loc || '') ? '' : loc || '', min_stock: num(p['Min Qty']), max_stock: num(p['Max Qty']),
      manufacturer: p['Manufacturer'] || '', category: p['Category'] || '', notes: p['Notes'] || '', track_qty: no(p['Track Quantity']) ? '0' : '',
    });
  }
  if (inactiveParts) left.push(`${inactiveParts} inactive parts`);

  // ── Vendors (Fullbay's built-in test vendor and "Shop Inventory" placeholder stay behind)
  const vAddr = new Map(by('vendorAddresses').filter(a => yes(a['Is Default']) || !no(a['Address Active'])).map(a => [a['Vendor ID'], a]));
  let skippedVendors = 0;
  const vendors: Row[] = [];
  for (const v of by('vendors')) {
    if (no(v['Vendor Active']) || /fullbay - test vendor|^shop inventory/i.test(v['Vendor Name'])) { skippedVendors++; continue; }
    const a = vAddr.get(v['Vendor ID']);
    vendors.push({
      name: v['Vendor Name'], phone: v['Vendor Phone'] || '', terms_days: termsDays(v['Credit Terms']),
      address: a ? [a['Address Line 1'], a['Address Line 2'], a['City'], [a['State'], a['Zip/Postal Code']].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '',
    });
  }
  if (skippedVendors) left.push(`${skippedVendors} inactive / Fullbay placeholder vendors`);

  return { customers, units, parts, vendors, left, files };
}
